import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, Users } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const PROFESSIONAL_FAQS = [
  {
    id: "pro-turnaround",
    q: "How fast will I receive my first draft?",
    a: "Your first draft is with you within **24 to 48 hours** of your order being confirmed.",
  },
  {
    id: "pro-guarantee",
    q: "What is the 60-Day Interview Guarantee?",
    a: "If you don't land an interview within **60 days** of receiving your final CV, we'll rewrite it for free **and** provide extra resources and support to help you land interviews faster.",
  },
  {
    id: "pro-writer",
    q: "Who writes my CV?",
    a: "Every CV is hand-written by an experienced UK CV writer who knows your sector — never AI-generated on a Professional package.",
  },
  {
    id: "pro-revisions",
    q: "How many revisions are included?",
    a: "**Unlimited revisions** until you are completely satisfied — plus free tweaks for 6–12 months after delivery.",
  },
  {
    id: "pro-industries",
    q: "Do you cover my industry?",
    a: "Yes — we cover NHS, IT, Finance, Marketing, HR, Engineering, Executive and dozens more. You'll be matched with a writer who knows your sector.",
  },
];

const AI_FAQS = [
  {
    id: "ai-what-is",
    q: "What does the £9.99 AI CV Builder actually give me?",
    a: "A guided wizard, a live preview, an ATS-friendly UK CV **and a matching cover letter** — downloadable as PDF & DOCX. Unlimited edits from your dashboard for 6 months.",
  },
  {
    id: "ai-quality",
    q: "Is the AI CV good enough to actually get interviews?",
    a: "Yes — the AI is trained on our own professional writing methodology and UK CV bestsellers. It's not a generic ChatGPT-style tool. Many users get interview invites within their first two weeks.",
  },
  {
    id: "ai-import",
    q: "Can I import my existing CV?",
    a: "Yes. Upload a PDF / DOCX / TXT (up to 5 MB) at Step 1 and we'll pre-fill the wizard automatically. Just review and fill any gaps.",
  },
  {
    id: "ai-credit",
    q: "What if I want a human writer later?",
    a: "Your **£9.99 comes off any Professional Writing package** — Standard CV, Standard Package, Premium CV or Premium Package. No wasted spend.",
  },
  {
    id: "ai-ats",
    q: "Is the AI CV ATS-friendly?",
    a: "Yes. Structure, formatting and keywords are all designed to pass Applicant Tracking Systems used by UK recruiters.",
  },
];

const MDLink = (props) => (
  <a
    {...props}
    className="text-brand underline hover:no-underline"
    target="_blank"
    rel="noopener noreferrer"
  />
);
const MDStrong = (props) => (
  <strong {...props} className="font-semibold text-foreground" />
);
const MD_COMPONENTS = { a: MDLink, strong: MDStrong };

export default function HomeFAQ() {
  const [tab, setTab] = useState("professional");
  const items = tab === "ai" ? AI_FAQS : PROFESSIONAL_FAQS;

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 md:py-32 bg-muted/30"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="label-eyebrow">FAQ</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            Everything you might be wondering.
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground/70">
            Answers for both routes — the £9.99 AI CV Builder and our
            Professional Writing service.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div
            data-testid="faq-tabs"
            className="inline-flex items-center gap-1 bg-background border border-border rounded-full p-1"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "professional"}
              data-testid="faq-tab-professional"
              onClick={() => setTab("professional")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                tab === "professional"
                  ? "bg-foreground text-background shadow"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              <Users className="h-4 w-4" />
              Professional CV Writing
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "ai"}
              data-testid="faq-tab-ai"
              onClick={() => setTab("ai")}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
                tab === "ai"
                  ? "bg-foreground text-background shadow"
                  : "text-foreground/70 hover:text-foreground"
              }`}
            >
              <Sparkles className="h-4 w-4" />
              AI CV Builder
            </button>
          </div>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {items.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              data-testid={`faq-item-${item.id}`}
              className="rounded-2xl border border-border bg-card px-5 md:px-6"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${item.id}`}
                className="text-left font-serif text-lg md:text-xl font-semibold hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-foreground/80 leading-relaxed pb-6">
                <div className="faq-answer space-y-3">
                  <ReactMarkdown components={MD_COMPONENTS}>
                    {item.a || ""}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
